import { Head, useAuth } from "zudoku/components";
import { useZudoku } from "zudoku/hooks";
import { useState, useEffect } from "react";

// ---------------------------------------------------------------------------
// Product links — configured via ZUDOKU_PUBLIC_MONTHLY_LINK_ID and
// ZUDOKU_PUBLIC_ANNUAL_LINK_ID environment variables
// ---------------------------------------------------------------------------
const ADD_ON_LINKS = {
  monthly: `https://buy.stripe.com/${import.meta.env.ZUDOKU_PUBLIC_MONTHLY_LINK_ID}`,
  annual: `https://buy.stripe.com/${import.meta.env.ZUDOKU_PUBLIC_ANNUAL_LINK_ID}`,
};

const ADD_ON_PACKS_ENDPOINT = "https://api.securitydatapoint.com/api/v1/addon-packs";

// ---------------------------------------------------------------------------
// Access helpers
// ---------------------------------------------------------------------------
type TokenPayload = Record<string, any>;

function decodeJwt(token: string): TokenPayload | null {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
  } catch {
    return null;
  }
}

function isEligible(payload: TokenPayload): boolean {
  const tier = (
    payload["https://api.securitydatapoint.com/tier"] ||
    payload.tier ||
    ""
  ).toLowerCase();
  const permission =
    payload["https://api.securitydatapoint.com/permission"] ||
    payload.permission ||
    "";
  const eligibleTiers = ["scale", "enterprise"];
  const isPlanAdmin = permission === "superadmin";
  return isPlanAdmin && eligibleTiers.includes(tier);
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------
interface AddonPacksData {
  billing_interval: "monthly" | "annual";
  available_packs: { id: string; name: string; description: string; price_monthly?: number; price_annual?: number }[];
}

export const AddOnPacksPage = () => {
  const auth = useAuth();
  const { authentication } = useZudoku();
  const [data, setData] = useState<AddonPacksData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // ---------- token helpers ----------
  const getAccessToken = async (): Promise<string | null> => {
    try {
      if (authentication?.getAccessToken) return await authentication.getAccessToken();
    } catch (e) {
      console.warn("getAccessToken() failed, falling back:", e);
    }
    return (
      (auth as any).providerData?.accessToken ||
      (auth as any).providerData?.access_token ||
      null
    );
  };

  // ---------- access gate from token ----------
  const getRawToken = (): string | null => {
    if (typeof localStorage === "undefined") return null;
    try {
      const raw = localStorage.getItem("auth-state");
      return JSON.parse(raw ?? "{}")?.state?.providerData?.accessToken ?? null;
    } catch {
      return null;
    }
  };

  const rawToken = getRawToken();
  const payload = rawToken ? decodeJwt(rawToken) : null;
  const eligible = payload ? isEligible(payload) : false;
  const userEmail =
    (auth as any).profile?.email ||
    auth.user?.email ||
    (payload?.["https://api.securitydatapoint.com/email"]) ||
    payload?.email ||
    "";

  // ---------- fetch add-on packs ----------
  useEffect(() => {
    if (!auth.isAuthenticated || !eligible) return;

    const fetchPacks = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const token = await getAccessToken();
        if (!token) throw new Error("No access token");
        const res = await fetch(ADD_ON_PACKS_ENDPOINT, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`API error ${res.status}: ${res.statusText}`);
        const json = await res.json();
        setData(json);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchPacks();
  }, [auth.isAuthenticated, eligible]);

  // ---------- unauthenticated ----------
  if (!auth.isAuthenticated) {
    return (
      <section className="max-w-3xl mx-auto px-4 py-12 text-center">
        <Head><title>Add-on Packs - SDP Developer Portal</title></Head>
        <h1 className="text-3xl font-bold mb-4">Add-on Packs</h1>
        <p className="text-gray-600 dark:text-gray-400">Please log in to access this page.</p>
      </section>
    );
  }

  // ---------- not eligible ----------
  if (!eligible) {
    return (
      <section className="max-w-3xl mx-auto px-4 py-12 text-center">
        <Head><title>Add-on Packs - SDP Developer Portal</title></Head>
        <h1 className="text-3xl font-bold mb-4">Add-on Packs</h1>
        <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">
            Access denied. This page is available to plan administrators on the Scale or Enterprise tier.
          </p>
        </div>
      </section>
    );
  }

  // ---------- loading ----------
  if (loading) {
    return (
      <section className="max-w-3xl mx-auto px-4 py-12 text-center">
        <Head><title>Add-on Packs - SDP Developer Portal</title></Head>
        <h1 className="text-3xl font-bold mb-4">Add-on Packs</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-6">Loading available add-ons…</p>
      </section>
    );
  }

  // ---------- fetch error ----------
  if (loadError) {
    return (
      <section className="max-w-3xl mx-auto px-4 py-12">
        <Head><title>Add-on Packs - SDP Developer Portal</title></Head>
        <h1 className="text-3xl font-bold mb-4">Add-on Packs</h1>
        <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">
            Failed to load add-on data: {loadError}
          </p>
        </div>
      </section>
    );
  }

  const billingInterval = data?.billing_interval ?? "monthly";
  const purchaseLink = ADD_ON_LINKS[billingInterval];


  return (
    <section className="max-w-3xl mx-auto px-4 py-12">
      <Head><title>Add-on Packs - SDP Developer Portal</title></Head>

      <div className="mb-10">
        <h1 className="text-4xl font-bold mb-3">Add-on Packs</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Expand your plan with additional capabilities. You are on the{" "}
          <span className="font-semibold capitalize">{billingInterval}</span> billing cycle.
        </p>
      </div>

      {/* Billing interval badge */}
      <div className="mb-8 inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm">
        <span className="text-blue-700 dark:text-blue-300 font-medium">
          Billing cycle:
        </span>
        <span className="capitalize text-blue-900 dark:text-blue-100 font-semibold">
          {billingInterval}
        </span>
      </div>

      {/* Purchase CTA */}
      <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <h2 className="text-xl font-semibold mb-2">Purchase Add-ons</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          Click below to purchase add-on packs for your{" "}
          <span className="font-medium capitalize">{billingInterval}</span> subscription via our
          secure checkout.
        </p>
        {billingInterval === "monthly" ? (
          <div className="flex flex-col sm:flex-row gap-8 items-start">
            {/* Option A: Stripe Buy Button web component (customer-email may not be supported) */}
            <div className="flex flex-col items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide"></span>
              <div style={{ minWidth: 288 }}>
                <script async src="https://js.stripe.com/v3/buy-button.js"></script>
                <stripe-buy-button
                  buy-button-id="buy_btn_1TEdKIAbz9XE7IPjxUkDvTaT"
                  publishable-key="pk_live_51Sjt9uAbz9XE7IPjvNseViL1kk0c65vBObSqrbuHeSxOCWCp0ZfFhHUGtLuMQaZXnsSNBJQlQmWwjkkczQ2nk6Vd00bG425EpE"
                  customer-email={userEmail}
                ></stripe-buy-button>
              </div>
            </div>
          </div>
        ) : (
          <a
            href={purchaseLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            Purchase Add-on Packs →
          </a>
        )}
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-500">
          You will be redirected to our secure payment provider. Add-ons are billed on your existing{" "}
          {billingInterval} cycle.
        </p>
      </div>
    </section>
  );
};
